import { CheckIcon } from "@heroicons/react/24/solid";

import classNames from "../../utils/classNames";
import { WIZARD_STEPS } from "./types";

interface WizardStepperProps {
  current: number;
  /** Highest step the user has reached; earlier steps are clickable. */
  maxReached: number;
  onSelect: (index: number) => void;
}

export default function WizardStepper({ current, maxReached, onSelect }: WizardStepperProps) {
  return (
    <nav aria-label="Progress">
      <ol className="flex items-center">
        {WIZARD_STEPS.map((step, index) => {
          const complete = index < current;
          const active = index === current;
          const reachable = index <= maxReached;
          return (
            <li
              key={step.id}
              className={classNames(index !== WIZARD_STEPS.length - 1 ? "pr-4 sm:pr-16 flex-1" : "", "relative")}
            >
              {index !== WIZARD_STEPS.length - 1 && (
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className={classNames("h-0.5 w-full", complete ? "bg-primary-600" : "bg-gray-200")} />
                </div>
              )}
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && onSelect(index)}
                className={classNames(
                  "relative flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold",
                  complete
                    ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700"
                    : active
                    ? "border-primary-600 bg-white text-primary-600"
                    : "border-gray-300 bg-white text-gray-400",
                  reachable ? "cursor-pointer" : "cursor-default"
                )}
                aria-current={active ? "step" : undefined}
              >
                {complete ? <CheckIcon className="h-5 w-5" aria-hidden="true" /> : index + 1}
                <span className="sr-only">{step.name}</span>
              </button>
              <span
                className={classNames(
                  "hidden sm:block absolute left-0 top-9 w-24 text-xs font-medium",
                  active ? "text-primary-600" : complete ? "text-gray-700" : "text-gray-400"
                )}
              >
                {step.name}
              </span>
            </li>
          );
        })}
      </ol>
      {/* On phones the per-step labels are hidden; show the active step's name instead. */}
      <p className="sm:hidden mt-3 text-xs font-medium text-primary-600">
        Step {current + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[current]?.name}
      </p>
    </nav>
  );
}
